/**
 * ToastContext - Lightweight global toast notifications
 *
 * Provides `useToast()` with `toast.success / toast.error / toast.info`
 * for transient feedback on user actions (sign out, saves, revokes, etc.).
 * Toasts stack top-right, auto-dismiss after 3.5s, and can be dismissed
 * manually. No external dependencies - plain context + Tailwind classes
 * matching the app's existing feedback boxes.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { clsx } from 'clsx';

type ToastType = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  /** True while the exit transition is playing. */
  leaving: boolean;
}

export interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS = 3500;
const EXIT_MS = 200;

const TOAST_STYLES: Record<ToastType, string> = {
  success: 'bg-success-500/20 border-success-500/30 text-success-400',
  error: 'bg-danger-500/20 border-danger-500/30 text-danger-400',
  info: 'bg-dark-800/90 border-primary-500/30 text-white/85',
};

const TOAST_ICONS: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertTriangle,
  info: Info,
};

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: () => void;
}) {
  // Start off-screen, then transition in on the next frame.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const visible = entered && !toast.leaving;
  const Icon = TOAST_ICONS[toast.type];

  return (
    <div
      role="status"
      className={clsx(
        'pointer-events-auto flex w-80 max-w-[calc(100vw-2rem)] items-start gap-2 rounded-lg border p-3 text-sm shadow-lg backdrop-blur-md transition-all duration-200',
        visible ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0',
        TOAST_STYLES[toast.type]
      )}
    >
      <Icon
        className={clsx(
          'mt-0.5 h-4 w-4 flex-shrink-0',
          toast.type === 'info' && 'text-primary-400'
        )}
      />
      <span className="flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="flex-shrink-0 rounded p-0.5 text-white/40 transition-colors hover:bg-white/10 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextIdRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    // Mark as leaving so the exit transition plays, then remove.
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, leaving: true } : t))
    );
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, EXIT_MS);
  }, []);

  const push = useCallback(
    (type: ToastType, message: string) => {
      const id = ++nextIdRef.current;
      setToasts((prev) => [...prev, { id, type, message, leaving: false }]);
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss]
  );

  const toast = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Below the fixed z-50 header (h-16-ish) so toasts never sit under it */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-4 top-20 z-50 flex flex-col items-end gap-2"
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
